<script setup lang="ts">
  import { ButtonVariant, themeVars } from '@tundr/theme';
  import { computed, useSlots } from 'vue';
  import { Button, ButtonSizes } from '../../Button';
  import { ChipVariant } from '../../Chip';
  import Chip from '../../Chip/Chip.vue';
  import { DataItem } from '../../DataItem';
  import {
    cardActionsWrapperClass,
    cardContainerClass,
    cardContentClass,
    cardDataWrapperClass,
    cardImageWrapperClass,
    cardItemExtraContentClass,
    cardTitleClass,
    cardWrapperClass,
    statusChipClass,
    withSideBorderClass,
  } from './CardListItem.css.ts';
  import type { CardListItemProps } from './CardListItem.types';
  import { Tooltip } from '@tundr/ds-components';

  const slots = useSlots();

  const hasExtraContentSlot = computed(
    () =>
      slots?.extraContent &&
      (slots.extraContent()[0].children as string).length,
  );

  const props = withDefaults(defineProps<CardListItemProps>(), {
    statusChipVariant: ChipVariant.TERTIARY,
    borderColor: themeVars.colors.brand.primary.x30 as string,
  });

  const cardWrapperClasses = computed(() => ({
    [cardWrapperClass]: true,
    [withSideBorderClass]: props.withSideBorder,
  }));
</script>

<template>
  <div
    :data-test-id="testId"
    :class="cardWrapperClasses"
    :style="{ borderColor: `${borderColor} !important` }"
  >
    <slot name="container">
      <div :class="cardContainerClass">
        <slot name="image">
          <Tooltip :title="imageTooltip">
            <div>
              <img
                v-if="image"
                :src="image"
                :class="cardImageWrapperClass"
                :width="imageWidth"
                :height="imageHeight"
              >
            </div>
          </Tooltip>
        </slot>

        <div :class="cardContentClass">
          <slot name="content">
            <slot name="title">
              <div :class="cardTitleClass">
                {{ title }}
                <Chip
                  :variant="statusChipVariant"
                  :class="statusChipClass"
                  squared
                  disable-uppercase
                  v-if="statusChip"
                >
                  {{ statusChip }}
                </Chip>
              </div>
            </slot>

            <div :class="cardDataWrapperClass">
              <DataItem
                v-for="data in dataList"
                :key="data.value"
                :label="data.label"
                :value="data.value"
              />
            </div>
          </slot>
        </div>

        <div :class="cardActionsWrapperClass">
          <slot name="actions">
            <Button
              v-for="button in actions"
              :key="button.label"
              :size="button.size || ButtonSizes.SMALL"
              :variant="button.variant || ButtonVariant.transparentOutlined"
              v-bind="button"
            />
          </slot>
        </div>
      </div>
    </slot>

    <div v-if="hasExtraContentSlot" :class="cardItemExtraContentClass">
      <slot name="extraContent" v-bind="props" />
    </div>
  </div>
</template>
